import React from 'react'
import { makeStyles } from '@mui/styles'
import clsx from 'clsx'
import PropTypes from 'prop-types'
import theme from '@/theme'

import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'
import CheckIcon from '@assets/images/icons/check.svg'
import { Title } from '@/modules/base/components/texts'
import { isMeinBuero } from '@/utils/build-env'
import RichTooltip from '@/modules/base/components/rich-tooltip/rich-tooltip'
import productSwitchMain from '@assets/images/icons/productswitch_Buchhaltung.png'
import productSwitchDocument from '@assets/images/product/document_image.png'
import productSwitchStaff from '@assets/images/product/staff_image.png'

const isMbo = isMeinBuero()

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    justifyContent: 'space-between',
    flexDirection: 'column',
    position: 'relative',
    cursor: 'pointer',
    width: '100%',
    border: theme.border,
    borderRadius: theme.borderRadius,
    transition: 'all 0.2s ease-in-out',
    color: theme.black,
    '&:hover': {
      borderColor: theme.primary,
      backgroundColor: theme.backgroundLight,
    },
  },
  content: {
    padding: '0 20px 20px 20px',
    display: 'flex',
    flexFlow: 'column nowrap',
    flexGrow: 1,
  },
  footer: {
    padding: '0 20px 20px 20px',
    display: 'flex',
    flexFlow: 'column nowrap',
  },
  active: ({ active }) => ({
    borderColor: `${active ? theme.primary : theme.borderColor} !important`,
    backgroundColor: theme.backgroundLight,
  }),
  disabled: {
    cursor: 'not-allowed',
    opacity: '0.5',
    '&:hover': {
      borderColor: theme.borderColor,
      backgroundColor: theme.white,
    },
  },
  iconContainer: ({ iconContainerHeight }) => ({
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    minHeight: iconContainerHeight,
    maxHeight: iconContainerHeight,
    alignItems: 'center',
    backgroundColor: theme.trialSelection.optionCard.backgroundColor,
    justifyContent: 'center',
    borderTopLeftRadius: theme.borderRadius,
    borderTopRightRadius: theme.borderRadius,

    [theme.devices.mobile]: {
      display: 'none',
    },
  }),
  selectionCircle: ({ active }) => ({
    position: 'absolute',
    top: 10,
    right: 10,
    width: 20,
    height: 20,
    border: theme.border,
    backgroundColor: active ? theme.primary : theme.white,
    borderRadius: '50%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: 'auto',
  }),
  divider: {
    width: '100%',
    height: 1,
    backgroundColor: theme.borderColor,
    margin: '15px 0',

    [theme.devices.mobile]: {
      marginTop: 0,
    },
  },
  tooltip: {
    height: 'fit-content',
    color: theme.black,
  },
  tooltipDescription: {
    fontSize: theme.fontSizes.smaller,
  },
  tooltipTitle: {
    fontSize: theme.fontSizes.default,
    fontWeight: theme.fontWeights.bold,
    marginBottom: 10,
  },
  tabContentIconContainer: {
    [theme.devices.mobile]: {
      display: 'none',
    },
  },
  tabContentIcon: {
    marginRight: 30,
  },
}))

const SelectionCard = (props) => {
  const {
    title,
    cardIcon,
    value,
    active,
    disabled,
    onClick,
    children,
    fill,
    className,
    classNameContent,
    classNameFooter,
    useCheckedIcon,
    useContentIcon,
    classNameContainer,
    iconContainer,
    footerChildren,
    hideDivider,
    iconHeight,
    iconWidth,
    iconContainerHeight,
    tooltip,
  } = props

  const classes = useStyles({ active, iconContainerHeight }, [active])
  const onClickHandler = disabled ? () => {} : () => onClick(value)
  const tabContentIcons = {
    document: productSwitchDocument,
    main: productSwitchMain,
    staff: productSwitchStaff,
  }

  let content = (
    <div
      onClick={onClickHandler}
      className={clsx(classes.container, classNameContainer, active && classes.active, disabled && classes.disabled)}>
      <div>
        <div>
          {(cardIcon || useCheckedIcon)
              && <div className={clsx(classes.iconContainer, iconContainer)}>
                {cardIcon && <SVGIcon svg={cardIcon} height={iconHeight} width={iconWidth} fill={fill} />}
                {
                  useCheckedIcon && <div className={classes.selectionCircle}>
                    {active && <SVGIcon svg={CheckIcon} height={'12px'} width={'12px'} fill={fill} />}
                  </div>
                }
              </div>
          }
          <div className={clsx(classes.content, classNameContent)}>
            {title && <Title className={className}>{title}</Title>}
            {children}
          </div>
        </div>
        {useContentIcon
            && <div className={classes.tabContentIconContainer}>
              <img className={classes.tabContentIcon} src={tabContentIcons[value]} height={'240px'} width={'340px'} />
            </div>
        }
      </div>

      <div className={clsx(classes.footer, classNameFooter)}>
        {!hideDivider && <div className={classes.divider} />}
        {footerChildren}
      </div>
    </div>
  )

  if (tooltip) {
    content = (<RichTooltip
      placement={'top'}
      width={300}
      content={(
        <div className={classes.tooltip}>
          <div className={classes.tooltipTitle}>
            {tooltip.title}
          </div>
          <div className={classes.tooltipDescription} dangerouslySetInnerHTML={{ __html: tooltip.description }}></div>
        </div>)
      }
    >
      {content}
    </RichTooltip>)
  }

  return content
}

SelectionCard.propTypes = {
  title: PropTypes.string,
  cardIcon: PropTypes.string,
  value: PropTypes.string,
  active: PropTypes.bool,
  disabled: PropTypes.bool,
  onClick: PropTypes.func,
  children: PropTypes.node,
  fill: PropTypes.string,
  className: PropTypes.string,
  classNameContent: PropTypes.string,
  classNameFooter: PropTypes.string,
  useCheckedIcon: PropTypes.bool,
  useContentIcon: PropTypes.bool,
  hideDivider: PropTypes.bool,
  footerChildren: PropTypes.node,
  iconHeight: PropTypes.string,
  iconWidth: PropTypes.string,
  iconContainerHeight: PropTypes.number,
  iconContainer: PropTypes.string,
  tooltip: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
  }),
}

SelectionCard.defaultProps = {
  fill: theme.white,
  cardIcon: '',
  className: '',
  classNameContent: '',
  classNameFooter: '',
  hideDivider: false,
  iconHeight: isMbo ? '72px' : '92px',
  iconWidth: '200px',
  iconContainerHeight: 115,
  useContentIcon: false,
}

export default SelectionCard
